import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import type { UseMultiplayerReturn } from '../../hooks/useMultiplayer.js';
import { keepObjectiveTicks } from '@roll1/shared';

type Changes = Record<string, unknown> & { readonly data?: Record<string, unknown> };

interface Draft {
  readonly requestId: string;
  readonly changes: Changes;
  /**
   * sent — awaiting the server's answer; offline — not sent, or its answer was
   * lost with the socket (an update is idempotent: sent again after the
   * rejoin); failed — refused, kept until saved again.
   */
  readonly state: 'sent' | 'offline' | 'failed';
  readonly error?: string;
}

/** What the GM sees about a text not yet confirmed. Absent — nothing unconfirmed. */
export interface JournalDraftStatus {
  readonly state: 'saving' | 'offline' | 'failed';
  readonly error?: string;
}

type Translate = (key: keyof TranslationKeys) => string;

/** Same text the table shows for a command:error (useMultiplayerSync). */
function errorText(msg: Record<string, unknown>, t: Translate): string {
  const raw = typeof msg.error === 'string' ? msg.error : '';
  const code = typeof msg.code === 'string' ? msg.code : '';
  const key = code !== '' ? `error.${code}` : '';
  const translated = key !== '' ? t(key as keyof TranslationKeys) : raw;
  return translated !== '' && translated !== key ? translated : raw;
}

function merge(a: Changes, b: Changes): Changes {
  return {
    ...a,
    ...b,
    ...(a.data !== undefined || b.data !== undefined ? { data: { ...a.data, ...b.data } } : {}),
  };
}

/**
 * `older` without the fields `saved` has just saved (top-level fields whole,
 * data keys one by one; arrays are whole values). Undefined when nothing of it
 * is left.
 */
function without(older: Changes, saved: Changes): Changes | undefined {
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(older)) {
    if (key === 'data' || key in saved) continue;
    rest[key] = value;
  }
  if (older.data !== undefined) {
    const data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(older.data)) {
      if (saved.data === undefined || !(key in saved.data)) data[key] = value;
    }
    if (Object.keys(data).length > 0) rest.data = data;
  }
  return Object.keys(rest).length > 0 ? (rest as Changes) : undefined;
}

/** What the GM intends for the entry: every edit in order, a later one winning per field. */
function intent(drafts: readonly Draft[]): Changes {
  return drafts.reduce((acc, d) => merge(acc, d.changes), {} as Changes);
}

/**
 * The current intent for exactly the fields these drafts changed (data keys
 * one by one). Re-sending a refused / offline edit this way never sends its
 * old value over a later edit of the same field, and keeps its other fields.
 */
function currentFor(all: readonly Draft[], these: readonly Draft[]): Changes {
  const now = intent(all);
  const out: Record<string, unknown> = {};
  let data: Record<string, unknown> | undefined;
  for (const draft of these) {
    for (const [key, value] of Object.entries(draft.changes)) {
      if (key !== 'data') {
        out[key] = now[key];
        continue;
      }
      data ??= {};
      for (const dataKey of Object.keys(value as Record<string, unknown>)) data[dataKey] = now.data?.[dataKey];
    }
  }
  return data !== undefined ? { ...out, data } : out;
}

type Objectives = Parameters<typeof keepObjectiveTicks>[1];

function apply<E extends { readonly data?: unknown }>(entry: E, drafts: readonly Draft[]): E {
  let out: Record<string, unknown> = { ...entry };
  for (const draft of drafts) {
    const { data, ...rest } = draft.changes;
    out = { ...out, ...rest, ...(data !== undefined ? { data: { ...(out.data as object), ...data } } : {}) };
  }
  // A quest's objectives typed here keep their text, but each tick is the
  // server's (GM Studio quests 1): another GM's tick shows under an edit too.
  const confirmed = (entry.data as { objectives?: unknown } | undefined)?.objectives;
  const shown = (out.data as { objectives?: unknown } | undefined)?.objectives;
  if (shown !== confirmed && Array.isArray(confirmed) && Array.isArray(shown)) {
    out = { ...out, data: { ...(out.data as object), objectives: keepObjectiveTicks(confirmed as Objectives, shown as Objectives) } };
  }
  return out as E;
}

/**
 * Journal edits made in Studio, until the server confirms them (GM Studio
 * package 3, review R1). Editors keep their own debounce and flush (package
 * 1.1); what they send goes through here: every journal:update carries a
 * requestId, the server echoes it to GMs (journal:updated) and in
 * command:error. Until then the edit is laid over the server state for EVERY
 * editor of that entry — the next editor of the same entry (the overview of
 * another beat sharing it, the document tab, the pinned pane) starts from the
 * text just typed, not from the snapshot before it, so continuing to type
 * never sends an older base. Nothing here is shown as saved: a refused edit
 * stays laid over (the draft is not destroyed) and is reported; an edit whose
 * answer was lost with the socket is sent again after the rejoin.
 */
export function useJournalDrafts<E extends { readonly id: string; readonly data?: unknown }>(
  mp: Pick<UseMultiplayerReturn, 'send' | 'onMessage' | 'status'> & {
    readonly session?: { readonly id: string } | null;
  },
  entries: readonly E[],
  t: Translate,
): {
  /** The entries as the editors should show them: server state + unconfirmed edits. */
  readonly entries: readonly E[];
  readonly send: (entryId: string, changes: Changes) => void;
  readonly status: (entryId: string) => JournalDraftStatus | undefined;
  /** Send a refused / offline text again (one update with all of it). */
  readonly resend: (entryId: string) => void;
} {
  const draftsRef = useRef(new Map<string, readonly Draft[]>());
  const [drafts, setDrafts] = useState<ReadonlyMap<string, readonly Draft[]>>(() => new Map());
  const mpRef = useRef(mp);
  mpRef.current = mp;
  const campaignId = mp.session?.id;

  const commit = useCallback((entryId: string, list: readonly Draft[]): void => {
    const next = new Map(draftsRef.current);
    if (list.length === 0) next.delete(entryId);
    else next.set(entryId, list);
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const push = useCallback(
    (entryId: string, changes: Changes, kept: readonly Draft[]): void => {
      const requestId = crypto.randomUUID();
      const online = mpRef.current.status === 'connected';
      commit(entryId, [...kept, { requestId, changes, state: online ? 'sent' : 'offline' }]);
      if (online) mpRef.current.send({ type: 'journal:update', entryId, changes, requestId } as never);
    },
    [commit],
  );

  const send = useCallback(
    (entryId: string, changes: Changes): void => push(entryId, changes, draftsRef.current.get(entryId) ?? []),
    [push],
  );

  const resend = useCallback(
    (entryId: string): void => {
      const list = draftsRef.current.get(entryId) ?? [];
      const again = list.filter((d) => d.state !== 'sent');
      if (again.length === 0) return;
      push(entryId, currentFor(list, again), list.filter((d) => d.state === 'sent'));
    },
    [push],
  );

  useEffect(() => {
    return mp.onMessage((incoming): void => {
      const msg = incoming as unknown as { type: string } & Record<string, unknown>;
      const requestId = typeof msg.requestId === 'string' ? msg.requestId : undefined;
      if (msg.type === 'journal:deleted' && typeof msg.entryId === 'string') {
        if (draftsRef.current.has(msg.entryId)) commit(msg.entryId, []);
        return;
      }
      if (requestId === undefined) return;
      if (msg.type !== 'journal:updated' && msg.type !== 'command:error') return;
      for (const [entryId, list] of draftsRef.current) {
        const index = list.findIndex((d) => d.requestId === requestId);
        if (index === -1) continue;
        const draft = list[index]!;
        if (msg.type === 'journal:updated') {
          // Confirmed: drop it, and drop exactly the fields it saved from every
          // EARLIER edit — an older (refused / unknown) value of a saved field
          // can no longer show or be sent again; the older edit's other fields
          // stay with their state. Later edits are newer than this save: kept.
          const earlier: Draft[] = [];
          for (const d of list.slice(0, index)) {
            const rest = without(d.changes, draft.changes);
            if (rest !== undefined) earlier.push({ ...d, changes: rest });
          }
          commit(entryId, [...earlier, ...list.slice(index + 1)]);
        } else {
          commit(entryId, list.map((d, i) => (i === index ? { ...d, state: 'failed' as const, error: errorText(msg, t) } : d)));
        }
        return;
      }
    });
  }, [mp, t, commit]);

  // Lost connection: answers of sent edits are lost too — they wait for the
  // rejoin («connected» comes with session:joined) and are sent again, merged
  // per entry. Setting the same text again is harmless.
  useEffect(() => {
    if (mp.status !== 'connected') {
      for (const [entryId, list] of draftsRef.current) {
        if (list.some((d) => d.state === 'sent')) {
          commit(entryId, list.map((d) => (d.state === 'sent' ? { ...d, state: 'offline' as const } : d)));
        }
      }
      return;
    }
    for (const [entryId, list] of draftsRef.current) {
      const offline = list.filter((d) => d.state === 'offline');
      if (offline.length === 0) continue;
      push(entryId, currentFor(list, offline), list.filter((d) => d.state !== 'offline'));
    }
  }, [mp.status, commit, push]);

  // Another campaign: none of these entries.
  useEffect(() => {
    return (): void => {
      draftsRef.current = new Map();
      setDrafts(draftsRef.current);
    };
  }, [campaignId]);

  const shown = useMemo(
    (): readonly E[] =>
      drafts.size === 0 ? entries : entries.map((e) => {
        const list = drafts.get(e.id);
        return list === undefined ? e : apply(e, list);
      }),
    [entries, drafts],
  );

  const status = useCallback(
    (entryId: string): JournalDraftStatus | undefined => {
      const list = drafts.get(entryId);
      if (list === undefined || list.length === 0) return undefined;
      const failed = list.find((d) => d.state === 'failed');
      if (failed !== undefined) return { state: 'failed', error: failed.error };
      if (list.some((d) => d.state === 'offline')) return { state: 'offline' };
      return { state: 'saving' };
    },
    [drafts],
  );

  return { entries: shown, send, status, resend };
}
